import L from 'leaflet'
import { useEffect, useRef } from 'react'

import { useLeafletMapContext } from '@/shared/components/LeafletMap'

import { useReplayContext } from '../HistoricalReplay/ReplayContext'
import { useDrawHistoricalTrace } from './useDrawHistoricalTrace'

/**
 * The following hook is used to set up and manage historical trace visualization on the map.
 * It handles activation of the trace drawing, fitting the map to the trace data,
 * and proper cleanup of map resources.
 * 
 * @returns void
 */
export const useHistoricalTrace = () => {
  const { map } = useLeafletMapContext()
  const {
    traces,
    activeTraceIndex,
    currentPointIndex,
    showProcessedPoints,
    showRawPoints,
    showRoute,
  } = useReplayContext()

  // Track if map bounds need to be adjusted when a new trace is selected
  const boundsSetRef = useRef<boolean>(false)

  // Use the optimized hook to draw historical trace data
  useDrawHistoricalTrace({
    map,
    traces,
    activeTraceIndex,
    currentPointIndex,
    showProcessedPoints,
    showRawPoints,
    showRoute,
  })

  // Fit map bounds to active trace when it changes
  useEffect(() => {
    if (!map || activeTraceIndex === null || traces.length === 0) {
      boundsSetRef.current = false

      return
    }

    // Skip if bounds were already set for this trace
    if (boundsSetRef.current) return

    const activeTrace = traces[activeTraceIndex]

    if (!activeTrace) return

    // Use all available points to calculate bounds
    const allPoints = [
      ...(showProcessedPoints ? activeTrace.tracePoints : []),
      ...(showRawPoints ? activeTrace.actualTracePoints : []),
      ...(showRoute && activeTrace.wayPoints ? activeTrace.wayPoints : []),
    ]

    if (allPoints.length === 0) return

    // Calculate bounds with optimization to avoid excessive calculations
    const bounds = L.latLngBounds([])
    const sampleRate = Math.max(1, Math.floor(allPoints.length / 100)) // Sample at most ~100 points for bounds

    for (let i = 0; i < allPoints.length; i += sampleRate) {
      const point = allPoints[i]

      if (point && point.x && point.y) {
        bounds.extend([point.y, point.x])
      }
    }

    // Add the last point to ensure complete coverage
    const lastPoint = allPoints[allPoints.length - 1]

    if (lastPoint && lastPoint.x && lastPoint.y) {
      bounds.extend([lastPoint.y, lastPoint.x])
    }

    // Fit map to bounds if valid with a smooth animation
    if (bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 16,
        animate: true,
        duration: 0.5,
      })
      boundsSetRef.current = true
    }
  }, [map, traces, activeTraceIndex, showProcessedPoints, showRawPoints, showRoute])

  // Reset bounds flag when active trace index changes
  useEffect(() => {
    boundsSetRef.current = false
  }, [activeTraceIndex])

  return null
}